// Theme toggle + language picker, shared by the landing topbar, auth pages
// and the app topbar. Both are round icon buttons; the language button shows
// the active language's flag and opens a small dropdown.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { Moon, Sun } from "./icons";
import { getTheme, toggleTheme } from "../lib/theme";
import { supportedLanguages, type LanguageCode } from "../i18n";

// Inline SVG flags — flag EMOJI are unreliable (Windows browsers render them
// as letter pairs), these look identical everywhere.
function Flag({ code, size = 18 }: { code: LanguageCode; size?: number }) {
  const common = {
    width: size,
    height: (size * 7) / 10,
    viewBox: "0 0 20 14",
    className: "rounded-[2px] shrink-0",
    "aria-hidden": true as const,
  };
  if (code === "de") {
    return (
      <svg {...common}>
        <rect width="20" height="14" fill="#000" />
        <rect y="4.67" width="20" height="4.67" fill="#dd0000" />
        <rect y="9.33" width="20" height="4.67" fill="#ffce00" />
      </svg>
    );
  }
  if (code === "sl") {
    return (
      <svg {...common}>
        <rect width="20" height="14" fill="#fff" />
        <rect y="4.67" width="20" height="4.67" fill="#2a5db0" />
        <rect y="9.33" width="20" height="4.67" fill="#d32011" />
        {/* simplified coat-of-arms: shield with the Triglav silhouette */}
        <path d="M4.2 1.6h3.6v3.2c0 1.3-.9 2.1-1.8 2.4-.9-.3-1.8-1.1-1.8-2.4z" fill="#2a5db0" stroke="#d32011" strokeWidth="0.3" />
        <path d="M4.7 5.4l1-1.5.5.6.4-.9 1.1 1.8c-.3.6-.9 1-1.7 1.2-.6-.2-1-.6-1.3-1.2z" fill="#fff" />
      </svg>
    );
  }
  // en — Union Jack, simplified
  return (
    <svg {...common}>
      <rect width="20" height="14" fill="#012169" />
      <path d="M0 0l20 14M20 0L0 14" stroke="#fff" strokeWidth="2.8" />
      <path d="M0 0l20 14M20 0L0 14" stroke="#c8102e" strokeWidth="1.2" />
      <path d="M10 0v14M0 7h20" stroke="#fff" strokeWidth="4.6" />
      <path d="M10 0v14M0 7h20" stroke="#c8102e" strokeWidth="2.8" />
    </svg>
  );
}

const ROUND_BTN =
  "inline-flex items-center justify-center h-8 w-8 rounded-full border border-border bg-panel2 text-muted hover:text-text transition";

function LanguagePicker() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const active = (i18n.language?.slice(0, 2) as LanguageCode) || "en";
  return (
    <div className="relative">
      <button
        type="button"
        className={ROUND_BTN}
        onClick={() => setOpen((o) => !o)}
        aria-label="Language"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={supportedLanguages.find((l) => l.code === active)?.label}
      >
        <Flag code={active} size={16} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-20 card p-1 min-w-[10rem]" role="listbox">
            {supportedLanguages.map((lang) => (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={lang.code === active}
                className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm rounded hover:bg-panel2"
                onClick={() => {
                  void i18n.changeLanguage(lang.code);
                  setOpen(false);
                }}
              >
                <Flag code={lang.code} />
                <span className="flex-1">{lang.label}</span>
                {lang.code === active && <Check size={14} className="text-brand" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function LangThemeControls() {
  const [theme, setThemeState] = useState(getTheme());
  return (
    <div className="flex items-center gap-2">
      <LanguagePicker />
      <button
        type="button"
        className={ROUND_BTN}
        onClick={() => {
          toggleTheme();
          setThemeState(getTheme());
        }}
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
      </button>
    </div>
  );
}
